import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Grid,
  Group,
  ThemeIcon,
  Table,
  Loader,
  Button,
  Badge,
  ScrollArea,
  Progress,
  Tabs,
  TextInput,
  Pagination,
} from "@mantine/core";
import {
  IconCalendar,
  IconDownload,
  IconRefresh,
  IconClock,
  IconAlertTriangle,
  IconCheck,
  IconSearch,
  IconEye,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDebouncedValue } from "@mantine/hooks";
import { usePerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import { DatePickerInput } from "@mantine/dates";

interface LoanSchedulingSummary {
  activeLoansCount: number;
  completedLoansCount: number;
  totalUpcomingAmount: number;
  totalOverdueAmount: number;
  upcomingPaymentsCount: number;
  overduePaymentsCount: number;
  onTimePaymentRate: number;
  loansMaturingSoon: number;
  averageDaysToNextPayment: number;
}

interface UpcomingPayment {
  loanId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  loanProduct: string;
  paymentNumber: number;
  dueDate: number;
  amount: number;
  daysUntilDue: number;
}

interface OverduePayment {
  loanId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  loanProduct: string;
  paymentNumber: number;
  dueDate: number;
  amount: number;
  daysOverdue: number;
}

interface PaymentSchedule {
  loanId: string;
  userId: string;
  userName: string;
  userEmail: string;
  loanProduct: string;
  principal: number;
  totalDue: number;
  amountPaid: number;
  remainingBalance: number;
  duration: number;
  interestRate: number;
  repaymentFrequency: string;
  progress: number;
  isCompleted: boolean;
  schedule: Array<{
    paymentNumber: number;
    dueDate: number;
    amount: number;
    principalPortion: number;
    interestPortion: number;
    status: "paid" | "overdue" | "upcoming" | "pending";
    paidAmount: number;
    paidDate?: number;
    daysOverdue: number;
    daysUntilDue: number | null;
  }>;
  createdAt: number;
}

interface PaymentByDay {
  date: string;
  count: number;
  amount: number;
}

interface LoanSchedulingResponse {
  summary: LoanSchedulingSummary;
  upcomingPayments: UpcomingPayment[];
  overduePayments: OverduePayment[];
  paymentSchedules: PaymentSchedule[];
  paymentsByDay: PaymentByDay[];
  loansMaturingSoon: PaymentSchedule[];
}

interface LoanScheduleListItem {
  _id: string;
  loanRequestId?: string;
  createdAt: number;
  approvedAt?: number;
  disbursedAt?: number;
  approvalType?: string;
  duration: number;
  amount: number;
  interestAmount: number;
  feesAmount: number;
  totalDueAmount: number;
  amountPaid: number;
  remainingBalance: number;
  status: "active" | "completed";
  loanProduct: {
    _id: string;
    name: string;
  };
  user: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
  };
  approvedBy?: {
    _id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  repaymentModel?: string;
  repaymentFrequency?: string;
}

interface LoanSchedulePagination {
  page: number;
  limit: number;
  totalRecords: number;
  totalPages: number;
}

interface LoanScheduleListResponse {
  loans: LoanScheduleListItem[];
  pagination: LoanSchedulePagination;
}

export default function LoanSchedulingReport() {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateRange, setDateRange] = useState<{ startDate?: number; endDate?: number }>({});
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [loanSearch, setLoanSearch] = useState("");
  const [loansPage, setLoansPage] = useState(1);
  const [debouncedLoanSearch] = useDebouncedValue(loanSearch, 400);
  const navigate = useNavigate();

  const {
    data: reportData,
    isLoading,
    reloadData,
  } = usePerformRequest<{ data: LoanSchedulingResponse }>({
    method: Endpoints.GetLoanSchedulingReport(
      dateRange.startDate,
      dateRange.endDate
    ).method,
    route: Endpoints.GetLoanSchedulingReport(
      dateRange.startDate,
      dateRange.endDate
    ).route,
  });

  const loanListEndpoint = Endpoints.GetLoanScheduleLoansList(
    dateRange.startDate,
    dateRange.endDate,
    loansPage,
    10,
    debouncedLoanSearch,
  );

  const {
    data: loanListResponse,
    isLoading: isLoanListLoading,
    reloadData: reloadLoanList,
  } = usePerformRequest<{ data: LoanScheduleListResponse }>({
    method: loanListEndpoint.method,
    route: loanListEndpoint.route,
  });

  useEffect(() => {
    const getTimestamp = (date: Date | null | string | unknown): number | undefined => {
      if (!date) return undefined;
      if (date instanceof Date) return date.getTime();
      if (typeof date === 'string') {
        const parsed = new Date(date);
        return isNaN(parsed.getTime()) ? undefined : parsed.getTime();
      }
      return undefined;
    };
    const start = getTimestamp(startDate);
    const end = getTimestamp(endDate);
    setDateRange({ startDate: start, endDate: end });
  }, [startDate, endDate]);

  useEffect(() => {
    reloadData();
  }, [dateRange]);

  useEffect(() => {
    reloadLoanList();
  }, [dateRange, loansPage, debouncedLoanSearch]);

  useEffect(() => {
    setLoansPage(1);
  }, [debouncedLoanSearch, dateRange.startDate, dateRange.endDate]);

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (timestamp?: number) => {
    if (!timestamp) return "—";
    return new Date(timestamp).toLocaleString("en-NG", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const summary = reportData?.data?.summary;
  const upcomingPayments = reportData?.data?.upcomingPayments || [];
  const overduePayments = reportData?.data?.overduePayments || [];
  const paymentSchedules = reportData?.data?.paymentSchedules || [];
  const paymentsByDay = reportData?.data?.paymentsByDay || [];
  const loansMaturingSoon = reportData?.data?.loansMaturingSoon || [];
  const approvedLoans = loanListResponse?.data?.loans || [];
  const loanPagination = loanListResponse?.data?.pagination;
  const loanTotalPages = loanPagination?.totalPages && loanPagination.totalPages > 0 ? loanPagination.totalPages : 1;
  const handleViewLoanReport = (loanId?: string) => {
    if (!loanId) return;
    navigate(`/dashboard/reports/loan-scheduling/${loanId}`);
  };

  const statCards = summary
    ? [
        {
          label: "Active Loans",
          value: summary.activeLoansCount.toLocaleString(),
          color: "#2563EB",
        },
        {
          label: "Upcoming Payments",
          value: summary.upcomingPaymentsCount.toLocaleString(),
          color: "#10B981",
        },
        {
          label: "Total Upcoming",
          value: formatCurrency(summary.totalUpcomingAmount),
          color: "#059669",
        },
        {
          label: "Overdue Payments",
          value: summary.overduePaymentsCount.toLocaleString(),
          color: "#DC2626",
        },
        {
          label: "Total Overdue",
          value: formatCurrency(summary.totalOverdueAmount),
          color: "#EF4444",
        },
        {
          label: "On-Time Rate",
          value: `${summary.onTimePaymentRate.toFixed(2)}%`,
          color: summary.onTimePaymentRate >= 80 ? "#10B981" : summary.onTimePaymentRate >= 60 ? "#F59E0B" : "#EF4444",
        },
        {
          label: "Loans Maturing Soon",
          value: summary.loansMaturingSoon.toLocaleString(),
          color: "#8B5CF6",
        },
        {
          label: "Avg Days to Next",
          value: `${summary.averageDaysToNextPayment.toFixed(0)} days`,
          color: "#6366F1",
        },
      ]
    : [];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <ThemeIcon size={48} radius="md" style={{ background: "#8B5CF6" }}>
              <IconCalendar size={24} color="white" />
            </ThemeIcon>
            <div>
              <Title order={2} fw={700}>
                Loan Scheduling Report
              </Title>
              <Text size="sm" c="dimmed">
                Track payment schedules, upcoming payments, and repayment performance
              </Text>
            </div>
          </Group>
          <Group gap="sm">
            <Button variant="light" leftSection={<IconRefresh size={16} />} onClick={() => reloadData()}>
              Refresh
            </Button>
            <Button variant="light" leftSection={<IconDownload size={16} />} disabled>
              Export
            </Button>
          </Group>
        </Group>

        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group gap="md">
            <DatePickerInput
              label="Start Date"
              placeholder="Select start date"
              value={startDate}
              onChange={(value) => setStartDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
            />
            <DatePickerInput
              label="End Date"
              placeholder="Select end date"
              value={endDate}
              onChange={(value) => setEndDate(value as unknown as Date | null)}
              leftSection={<IconCalendar size={16} />}
              clearable
              minDate={
                startDate
                  ? startDate instanceof Date
                    ? new Date(startDate.getTime() + 86400000)
                    : new Date(new Date(startDate as string).getTime() + 86400000)
                  : undefined
              }
            />
          </Group>
        </Paper>

        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <div>
                <Title order={4} fw={700}>
                  Approved Loans
                </Title>
                <Text size="sm" c="dimmed">
                  Every approved loan with quick access to its repayment schedule
                </Text>
              </div>
              <TextInput
                placeholder="Search for anything"
                leftSection={<IconSearch size={16} />}
                value={loanSearch}
                onChange={(event) => setLoanSearch(event.currentTarget.value)}
                style={{ maxWidth: 280 }}
              />
            </Group>

            <ScrollArea>
              <Table.ScrollContainer minWidth={1100}>
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>User</Table.Th>
                      <Table.Th>Loan Product</Table.Th>
                      <Table.Th>Date</Table.Th>
                      <Table.Th style={{ textAlign: "right" }}>Principal (NGN)</Table.Th>
                      <Table.Th style={{ textAlign: "right" }}>Interest (NGN)</Table.Th>
                      <Table.Th style={{ textAlign: "right" }}>Fees + Penalty (NGN)</Table.Th>
                      <Table.Th style={{ textAlign: "right" }}>Total (NGN)</Table.Th>
                      <Table.Th style={{ textAlign: "right" }}>Paid (NGN)</Table.Th>
                      <Table.Th style={{ textAlign: "right" }}>Balance (NGN)</Table.Th>
                      <Table.Th>Status</Table.Th>
                      <Table.Th />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {isLoanListLoading ? (
                      <Table.Tr>
                        <Table.Td colSpan={11}>
                          <Group justify="center">
                            <Loader size="sm" />
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ) : approvedLoans.length > 0 ? (
                      approvedLoans.map((loan) => {
                        const loanId = loan.loanRequestId || loan._id;
                        const rowKey = loanId || `${loan.createdAt}-${loan.user?._id || ""}`;
                        return (
                          <Table.Tr key={rowKey}>
                          <Table.Td>
                            <Stack gap={2}>
                              <Text size="sm" fw={600}>
                                {`${loan.user?.firstName || ""} ${loan.user?.lastName || ""}`.trim() || "N/A"}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {loan.user?.phone || loan.user?.email || "—"}
                              </Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{loan.loanProduct?.name || "—"}</Text>
                            <Text size="xs" c="dimmed" tt="uppercase">
                              {loan.repaymentModel?.replace(/_/g, " ") || "—"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={2}>
                              <Text size="sm">{formatDateTime(loan.disbursedAt || loan.approvedAt || loan.createdAt)}</Text>
                              <Text size="xs" c="dimmed">
                                {loan.approvalType ? loan.approvalType.toUpperCase() : "MANUAL"}
                              </Text>
                            </Stack>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(loan.amount)}</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(loan.interestAmount)}</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(loan.feesAmount)}</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600}>{formatCurrency(loan.totalDueAmount)}</Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600} c="green">
                              {formatCurrency(loan.amountPaid)}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ textAlign: "right" }}>
                            <Text fw={600} c={loan.remainingBalance > 0 ? "orange" : "green"}>
                              {formatCurrency(loan.remainingBalance)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={loan.status === "completed" ? "green" : "yellow"} variant="light">
                              {loan.status === "completed" ? "Completed" : "Active"}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Button
                              component={Link}
                              to={loanId ? `/dashboard/reports/loan-scheduling/${loanId}` : "#"}
                              size="xs"
                              variant="light"
                              leftSection={<IconEye size={14} />}
                              disabled={!loanId}
                              onClick={(event) => {
                                if (!loanId) {
                                  event.preventDefault();
                                  handleViewLoanReport(loanId);
                                }
                              }}
                            >
                              View report
                            </Button>
                          </Table.Td>
                          </Table.Tr>
                        );
                      })
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={11}>
                          <Stack gap={4} align="center" py="md">
                            <IconCalendar size={32} color="#9CA3AF" />
                            <Text size="sm" c="dimmed">
                              No approved loans found for the selected filters.
                            </Text>
                          </Stack>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </ScrollArea>

            {loanPagination && loanPagination.totalRecords > 0 && (
              <Group justify="space-between" align="center">
                <Text size="sm" c="dimmed">
                  Showing {(loanPagination.page - 1) * loanPagination.limit + 1}-
                  {Math.min(loanPagination.page * loanPagination.limit, loanPagination.totalRecords)} of{" "}
                  {loanPagination.totalRecords.toLocaleString()} loans
                </Text>
                <Pagination value={loansPage} onChange={setLoansPage} total={loanTotalPages} size="sm" />
              </Group>
            )}
          </Stack>
        </Paper>

        {isLoading ? (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Group justify="center">
              <Loader size="lg" />
            </Group>
          </Paper>
        ) : summary ? (
          <>
            <Grid gutter="md">
              {statCards.map((card, index) => (
                <Grid.Col key={card.label} span={{ base: 12, sm: 6, md: 3 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Paper shadow="sm" p="lg" radius="md" withBorder style={{ height: "100%" }}>
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed" fw={500}>
                          {card.label}
                        </Text>
                        <Text size="xl" fw={700} style={{ color: card.color }}>
                          {card.value}
                        </Text>
                      </Stack>
                    </Paper>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>

            <Tabs value={activeTab} onChange={(value) => setActiveTab(value || "overview")}>
              <Tabs.List>
                <Tabs.Tab value="overview" leftSection={<IconCalendar size={16} />}>
                  Overview
                </Tabs.Tab>
                <Tabs.Tab value="upcoming" leftSection={<IconClock size={16} />}>
                  Upcoming Payments
                </Tabs.Tab>
                <Tabs.Tab value="overdue" leftSection={<IconAlertTriangle size={16} />}>
                  Overdue Payments
                </Tabs.Tab>
                <Tabs.Tab value="schedules" leftSection={<IconCheck size={16} />}>
                  Payment Schedules
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="overview" pt="md">
                <Stack gap="md">
                  {/* Payments by Day */}
                  {paymentsByDay.length > 0 && (
                    <Paper shadow="sm" p="lg" radius="md" withBorder>
                      <Title order={4} mb="md">
                        Upcoming Payments by Day
                      </Title>
                      <Table.ScrollContainer minWidth={500}>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>Date</Table.Th>
                              <Table.Th>Payment Count</Table.Th>
                              <Table.Th style={{ textAlign: "right" }}>Total Amount</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {paymentsByDay.map((item, index) => (
                              <Table.Tr key={index}>
                                <Table.Td>
                                  <Text fw={500}>{formatDate(new Date(item.date).getTime())}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge variant="light">{item.count}</Badge>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text fw={600}>{formatCurrency(item.amount)}</Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                    </Paper>
                  )}

                  {/* Loans Maturing Soon */}
                  {loansMaturingSoon.length > 0 && (
                    <Paper shadow="sm" p="lg" radius="md" withBorder>
                      <Title order={4} mb="md">
                        Loans Maturing Soon (Next 30 Days)
                      </Title>
                      <ScrollArea>
                        <Table.ScrollContainer minWidth={800}>
                          <Table striped highlightOnHover>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>User</Table.Th>
                                <Table.Th>Loan Product</Table.Th>
                                <Table.Th style={{ textAlign: "right" }}>Principal</Table.Th>
                                <Table.Th style={{ textAlign: "right" }}>Remaining</Table.Th>
                                <Table.Th>Progress</Table.Th>
                                <Table.Th>Status</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {loansMaturingSoon.map((loan) => (
                                <Table.Tr key={loan.loanId}>
                                  <Table.Td>
                                    <Stack gap={2}>
                                      <Text size="sm" fw={500}>
                                        {loan.userName || "N/A"}
                                      </Text>
                                      <Text size="xs" c="dimmed">
                                        {loan.userEmail}
                                      </Text>
                                    </Stack>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size="sm">{loan.loanProduct}</Text>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: "right" }}>
                                    <Text size="sm" fw={600}>
                                      {formatCurrency(loan.principal)}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td style={{ textAlign: "right" }}>
                                    <Text size="sm" fw={600} c={loan.remainingBalance > 0 ? "orange" : "green"}>
                                      {formatCurrency(loan.remainingBalance)}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Progress value={loan.progress} size="sm" color={loan.progress >= 90 ? "green" : loan.progress >= 50 ? "yellow" : "blue"} />
                                    <Text size="xs" c="dimmed" mt={4}>
                                      {loan.progress.toFixed(1)}%
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Badge color={loan.isCompleted ? "green" : "blue"}>
                                      {loan.isCompleted ? "Completed" : "Active"}
                                    </Badge>
                                  </Table.Td>
                                </Table.Tr>
                              ))}
                            </Table.Tbody>
                          </Table>
                        </Table.ScrollContainer>
                      </ScrollArea>
                    </Paper>
                  )}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="upcoming" pt="md">
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                  <Group justify="space-between" mb="md">
                    <Title order={4}>
                      <Group gap="xs">
                        <IconClock size={20} />
                        Upcoming Payments (Next 90 Days)
                      </Group>
                    </Title>
                    <Badge color="green" size="lg">
                      {upcomingPayments.length} payments
                    </Badge>
                  </Group>
                  {upcomingPayments.length > 0 ? (
                    <ScrollArea>
                      <Table.ScrollContainer minWidth={900}>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>User</Table.Th>
                              <Table.Th>Loan Product</Table.Th>
                              <Table.Th>Payment #</Table.Th>
                              <Table.Th>Due Date</Table.Th>
                              <Table.Th>Days Until Due</Table.Th>
                              <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {upcomingPayments.map((payment, index) => (
                              <Table.Tr key={`${payment.loanId}-${payment.paymentNumber}-${index}`}>
                                <Table.Td>
                                  <Stack gap={2}>
                                    <Text size="sm" fw={500}>
                                      {payment.userName || "N/A"}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {payment.userEmail}
                                    </Text>
                                  </Stack>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{payment.loanProduct}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge variant="light">{payment.paymentNumber}</Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{formatDate(payment.dueDate)}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge color={payment.daysUntilDue <= 7 ? "red" : payment.daysUntilDue <= 30 ? "yellow" : "green"}>
                                    {payment.daysUntilDue} days
                                  </Badge>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text size="sm" fw={600}>
                                    {formatCurrency(payment.amount)}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                    </ScrollArea>
                  ) : (
                    <Text c="dimmed" ta="center" py="xl">
                      No upcoming payments found
                    </Text>
                  )}
                </Paper>
              </Tabs.Panel>

              <Tabs.Panel value="overdue" pt="md">
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                  <Group justify="space-between" mb="md">
                    <Title order={4}>
                      <Group gap="xs">
                        <IconAlertTriangle size={20} color="#DC2626" />
                        Overdue Payments
                      </Group>
                    </Title>
                    <Badge color="red" size="lg">
                      {overduePayments.length} payments
                    </Badge>
                  </Group>
                  {overduePayments.length > 0 ? (
                    <ScrollArea>
                      <Table.ScrollContainer minWidth={900}>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>User</Table.Th>
                              <Table.Th>Loan Product</Table.Th>
                              <Table.Th>Payment #</Table.Th>
                              <Table.Th>Due Date</Table.Th>
                              <Table.Th>Days Overdue</Table.Th>
                              <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {overduePayments.map((payment, index) => (
                              <Table.Tr key={`${payment.loanId}-${payment.paymentNumber}-${index}`}>
                                <Table.Td>
                                  <Stack gap={2}>
                                    <Text size="sm" fw={500}>
                                      {payment.userName || "N/A"}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {payment.userEmail}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                      {payment.userPhone}
                                    </Text>
                                  </Stack>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{payment.loanProduct}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge variant="light">{payment.paymentNumber}</Badge>
                                </Table.Td>
                                <Table.Td>
                                  <Text size="sm">{formatDate(payment.dueDate)}</Text>
                                </Table.Td>
                                <Table.Td>
                                  <Badge color="red">
                                    {payment.daysOverdue} days
                                  </Badge>
                                </Table.Td>
                                <Table.Td style={{ textAlign: "right" }}>
                                  <Text size="sm" fw={600} c="red">
                                    {formatCurrency(payment.amount)}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </Table.ScrollContainer>
                    </ScrollArea>
                  ) : (
                    <Text c="dimmed" ta="center" py="xl">
                      No overdue payments found
                    </Text>
                  )}
                </Paper>
              </Tabs.Panel>

              <Tabs.Panel value="schedules" pt="md">
                <Paper shadow="sm" p="lg" radius="md" withBorder>
                  <Title order={4} mb="md">
                    Payment Schedules
                  </Title>
                  {paymentSchedules.length > 0 ? (
                    <Stack gap="md">
                      {paymentSchedules.map((loan) => (
                        <Paper key={loan.loanId} p="md" withBorder radius="md">
                          <Stack gap="md">
                            <Group justify="space-between">
                              <div>
                                <Text fw={600} size="lg">
                                  {loan.userName} - {loan.loanProduct}
                                </Text>
                                <Text size="sm" c="dimmed">
                                  {loan.userEmail}
                                </Text>
                              </div>
                              <Badge color={loan.isCompleted ? "green" : "blue"} size="lg">
                                {loan.isCompleted ? "Completed" : "Active"}
                              </Badge>
                            </Group>
                            <Grid>
                              <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Principal</Text>
                                <Text fw={600}>{formatCurrency(loan.principal)}</Text>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Total Due</Text>
                                <Text fw={600}>{formatCurrency(loan.totalDue)}</Text>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Amount Paid</Text>
                                <Text fw={600} c="green">{formatCurrency(loan.amountPaid)}</Text>
                              </Grid.Col>
                              <Grid.Col span={6}>
                                <Text size="sm" c="dimmed">Remaining</Text>
                                <Text fw={600} c="orange">{formatCurrency(loan.remainingBalance)}</Text>
                              </Grid.Col>
                            </Grid>
                            <div>
                              <Group justify="space-between" mb="xs">
                                <Text size="sm" fw={500}>Repayment Progress</Text>
                                <Text size="sm" c="dimmed">{loan.progress.toFixed(1)}%</Text>
                              </Group>
                              <Progress value={loan.progress} size="lg" color={loan.progress >= 90 ? "green" : loan.progress >= 50 ? "yellow" : "blue"} />
                            </div>
                            <ScrollArea>
                              <Table.ScrollContainer minWidth={800}>
                                <Table striped>
                                  <Table.Thead>
                                    <Table.Tr>
                                      <Table.Th>#</Table.Th>
                                      <Table.Th>Due Date</Table.Th>
                                      <Table.Th style={{ textAlign: "right" }}>Amount</Table.Th>
                                      <Table.Th>Status</Table.Th>
                                      <Table.Th>Paid Date</Table.Th>
                                    </Table.Tr>
                                  </Table.Thead>
                                  <Table.Tbody>
                                    {loan.schedule.map((payment) => (
                                      <Table.Tr key={payment.paymentNumber}>
                                        <Table.Td>{payment.paymentNumber}</Table.Td>
                                        <Table.Td>{formatDate(payment.dueDate)}</Table.Td>
                                        <Table.Td style={{ textAlign: "right" }}>
                                          {formatCurrency(payment.amount)}
                                        </Table.Td>
                                        <Table.Td>
                                          <Badge
                                            color={
                                              payment.status === "paid"
                                                ? "green"
                                                : payment.status === "overdue"
                                                ? "red"
                                                : payment.status === "upcoming"
                                                ? "yellow"
                                                : "gray"
                                            }
                                          >
                                            {payment.status.toUpperCase()}
                                          </Badge>
                                        </Table.Td>
                                        <Table.Td>
                                          {payment.paidDate ? (
                                            <Text size="sm">{formatDate(payment.paidDate)}</Text>
                                          ) : (
                                            <Text size="sm" c="dimmed">-</Text>
                                          )}
                                        </Table.Td>
                                      </Table.Tr>
                                    ))}
                                  </Table.Tbody>
                                </Table>
                              </Table.ScrollContainer>
                            </ScrollArea>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  ) : (
                    <Text c="dimmed" ta="center" py="xl">
                      No payment schedules found
                    </Text>
                  )}
                </Paper>
              </Tabs.Panel>
            </Tabs>
          </>
        ) : (
          <Paper shadow="sm" p="xl" radius="md" withBorder>
            <Text c="dimmed" ta="center">
              No data available
            </Text>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}

